import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/_promo/components';
import { ArrowButton } from '@/components/_promo/components/ArrowButton';
import {
  DashedCopyCode,
  type TBorderStyleButton,
  type TCopyCodeButtonColor,
} from '@/components/_promo/components/DashedCopyCode';
import { Loader } from '@/components/_promo/components/Loader';
import { ImageComponent } from '@/components/Image';
import { replaceFsCount, useButtonAction } from '@/modules/promo';
import type { TButtonColor } from '../../templates/BanksBenefitsTemplate';
import { Cards } from './components/Cards';
import { Coins } from './components/Coins';
import { OfferTerms } from './components/OfferTerms';
import {
  getBenefitsData,
  getButtonConfig,
  getTheme,
  type OfferCardConfig,
} from './helpers';

import { ScOfferCard } from './styled';

export interface IOfferCardProps {
  translation: string;
  bonusCode?: string;
  validDate?: string;
  animatedButton?: boolean;
  shimmerButton?: boolean;
  buttonColor?: TButtonColor;
  copyCodeBtnColor?: TCopyCodeButtonColor;
  borderCopyCodeBtn?: TBorderStyleButton;
  srcImgs: string;
  withCards?: boolean;
  cardTheme?: OfferCardConfig;
  themeName?: string;
  coins?: ('coins-1' | 'coins-2' | 'coins-3')[];
  withoutBackImgs?: boolean;
  coinSizes?: Partial<
    Record<'coins-1' | 'coins-2' | 'coins-3', { width: number; height: number }>
  >;
  coinPositions?: Partial<
    Record<
      'coins-1' | 'coins-2' | 'coins-3',
      { left?: string; right?: string; top?: string; bottom?: string }
    >
  >;
  fsCount?: number;
  defaultFsCount?: number;
}

export const OfferCard = ({
  translation,
  bonusCode,
  validDate,
  animatedButton,
  shimmerButton,
  buttonColor,
  copyCodeBtnColor,
  srcImgs,
  borderCopyCodeBtn,
  cardTheme,
  withCards,
  themeName = 'default',
  coins = ['coins-1', 'coins-2', 'coins-3'],
  withoutBackImgs,
  coinSizes,
  fsCount,
  defaultFsCount,
  coinPositions,
}: IOfferCardProps) => {
  const { t } = useTranslation(translation);
  const [copyActive, setCopyActive] = useState(false);
  const [isCardRotate, setIsCardRotate] = useState(false);
  const { buttonAction, isLoading } = useButtonAction();
  const theme = getTheme(cardTheme, themeName);
  const activeButtonColor =
    buttonColor || (theme.regButton.color as TButtonColor);
  const buttonColorConfig = getButtonConfig();

  return (
    <ScOfferCard
      cardRotate={isCardRotate}
      cardTheme={theme}
      coinPositions={coinPositions}
      withCards={withCards}
    >
      <div className="card-inner">
        <div className="card-front">
          <div className="title">{t('offerCard.title')}</div>
          <div className="offer-count-fs">
            {replaceFsCount(t('offerCard.count.fs', { fs: fsCount }), fsCount)}
          </div>
          <div className="offer-fs">{t('offerCard.fs')}</div>
          <div className="benefits-container">
            {getBenefitsData(t, srcImgs).map(({ img, alt, text }, index) => (
              <div className="benefits-wrapper" key={index}>
                <ImageComponent alt={alt} height={40} src={img} width={40} />
                <span>{text}</span>
              </div>
            ))}
          </div>
          <Coins coinSizes={coinSizes} coins={coins} srcImgs={srcImgs} />

          {bonusCode && (
            <div className="copy-code-wrapper">
              <DashedCopyCode
                borderStyle={borderCopyCodeBtn}
                buttonColor={copyCodeBtnColor}
                code={bonusCode}
                copyActive={copyActive}
                setCopyActive={setCopyActive}
              />
            </div>
          )}

          <div className="reg-btn">
            <Button
              disabled={isLoading}
              {...buttonColorConfig[activeButtonColor]}
              {...buttonAction}
              animated={animatedButton}
              className="register-btn"
              shimmer={shimmerButton}
              wide
              xLarge
            >
              {isLoading ? <Loader /> : t('joinNow')}
            </Button>
          </div>

          <ArrowButton isOpen={false} onClick={() => setIsCardRotate(true)}>
            {t('common:button.termsAndConditions')}
          </ArrowButton>
          {withCards && <Cards />}
        </div>
        <div className="card-back">
          <OfferTerms
            defaultFsCount={defaultFsCount}
            isCardRotate={isCardRotate}
            translation={translation}
            validDate={validDate}
          />

          <ArrowButton isOpen={true} onClick={() => setIsCardRotate(false)}>
            {t('offerCard.back.button')}
          </ArrowButton>
          {!withoutBackImgs && (
            <Coins coinSizes={coinSizes} coins={coins} srcImgs={srcImgs} />
          )}
        </div>
      </div>
    </ScOfferCard>
  );
};
